import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { AuthenticatedFileLinks } from "../components/AuthenticatedFileLinks";
import { getApiBaseUrl } from "../config/apiBase";
import { useAppState } from "../context/AppStateContext";
import type { SubcontractorMaster } from "../types";
import { readFileAsRawBase64 } from "../utils/readFileAsRawBase64";
import {
  emptySubcontractorMasterDraft,
  isValidSubcontractorIfsc,
  normalizeSubcontractorAadhaarDigits,
} from "../utils/subcontractor";
import {
  fetchGstCaptchaSession,
  type GstTaxpayerDetails,
  isValidGstin,
  verifyGstinWithCaptcha,
} from "../api/gstVerifyApi";
import { lookupIfsc } from "../api/ifscLookupApi";

const IFSC_LOOKUP_OK_HINT = "Bank and branch filled from IFSC (you may edit).";

/* ── Types ──────────────────────────────────────────────────── */

type Draft = Omit<SubcontractorMaster, "createdAt">;

type GstState = { status: "idle" } | { status: "verified"; details: GstTaxpayerDetails };

function emptyDraft(): Draft {
  return { id: "", ...emptySubcontractorMasterDraft() };
}

type DocPicks = { cc: File | null; ad: File | null; gst: File | null; udyam: File | null; labour: File | null };

function isFormComplete(d: Draft, picks: DocPicks, mode: "new" | "edit"): boolean {
  const gstin = d.gstNumber.trim().toUpperCase();
  if (gstin.length > 0 && !isValidGstin(gstin)) return false;
  const aadhaar = normalizeSubcontractorAadhaarDigits(d.aadhaarNumber);
  const ccOk = picks.cc != null || (mode === "edit" && Boolean(d.cancelChequeStored));
  const adOk = picks.ad != null || (mode === "edit" && Boolean(d.aadharDocStored));
  const udyamOk = picks.udyam != null || (mode === "edit" && Boolean(d.udyamMsmeStored));
  const labourOk = picks.labour != null || (mode === "edit" && Boolean(d.labourLicenseStored));
  return Boolean(
    d.contactPersonName.trim() &&
      d.firmName.trim() &&
      d.contactPhone.trim() &&
      /^[0-9]{12}$/.test(aadhaar) &&
      d.bankAccountName.trim() &&
      d.bankBranchName.trim() &&
      d.bankName.trim() &&
      d.bankAccountNumber.trim() &&
      isValidSubcontractorIfsc(d.bankIfsc) &&
      ccOk &&
      adOk &&
      udyamOk &&
      labourOk,
  );
}

/* ── GST status badge ───────────────────────────────────────── */

function GstStatusBadge({ status }: { status: string }) {
  const lower = status.toLowerCase();
  if (lower === "active") {
    return <span className="status-chip status-chip--done" style={{ fontSize: "0.72rem" }}>Active</span>;
  }
  if (lower.includes("cancel")) {
    return <span className="status-chip status-chip--danger" style={{ fontSize: "0.72rem" }}>Cancelled</span>;
  }
  return <span className="status-chip" style={{ fontSize: "0.72rem" }}>{status}</span>;
}

/* ── GST verified info panel ────────────────────────────────── */

function GstVerifiedPanel({ details }: { details: GstTaxpayerDetails }) {
  const manualOnly = Boolean(details.portalLookupFailed && details.checksumValid);
  return (
    <div className={`gst-verified-panel${manualOnly ? " gst-verified-panel--manual" : ""}`}>
      <div className="gst-verified-panel__header">
        {manualOnly ? (
          <span className="gst-verified-panel__title gst-verified-panel__title--warn">GSTIN format OK — manual entry</span>
        ) : (
          <>
            <svg className="gst-verified-panel__icon" width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>GST verified</span>
            <GstStatusBadge status={details.gstStatus} />
          </>
        )}
      </div>
      {manualOnly ? (
        <p className="gst-verified-panel__manual-lead">
          The GST portal did not return business details (it often blocks automated requests). Your GSTIN check digit is
          valid. Open{" "}
          <a href="https://services.gst.gov.in/services/quicklinks/searchtxp" target="_blank" rel="noopener noreferrer">
            GST taxpayer search
          </a>{" "}
          in a browser, confirm the legal/trade name, then enter the firm name and other fields in Step 2 below.
        </p>
      ) : null}
      {!manualOnly ? (
        <dl className="gst-verified-panel__grid">
          {details.legalName && (
            <>
              <dt>Legal name</dt>
              <dd>{details.legalName}</dd>
            </>
          )}
          {details.tradeName && details.tradeName !== details.legalName && (
            <>
              <dt>Trade name</dt>
              <dd>{details.tradeName}</dd>
            </>
          )}
          {details.constitutionOfBusiness && (
            <>
              <dt>Constitution</dt>
              <dd>{details.constitutionOfBusiness}</dd>
            </>
          )}
          {details.taxpayerType && (
            <>
              <dt>Taxpayer type</dt>
              <dd>{details.taxpayerType}</dd>
            </>
          )}
          {details.registrationDate && (
            <>
              <dt>Registered on</dt>
              <dd>{details.registrationDate}</dd>
            </>
          )}
          {details.principalAddress && (
            <>
              <dt>Principal address</dt>
              <dd>{details.principalAddress}</dd>
            </>
          )}
          {details.additionalAddresses && details.additionalAddresses.length > 0 && (
            <>
              <dt>Additional places</dt>
              <dd>
                <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                  {details.additionalAddresses.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </dd>
            </>
          )}
          {details.stateJurisdiction && (
            <>
              <dt>State jurisdiction</dt>
              <dd>{details.stateJurisdiction}</dd>
            </>
          )}
        </dl>
      ) : null}
    </div>
  );
}

/* ── Form ───────────────────────────────────────────────────── */

function SubcontractorForm({
  mode,
  draft,
  setDraft,
  onSave,
  onCancel,
}: {
  mode: "new" | "edit";
  draft: Draft;
  setDraft: React.Dispatch<React.SetStateAction<Draft>>;
  onSave: (row: Omit<SubcontractorMaster, "id" | "createdAt">) => Promise<void>;
  onCancel: () => void;
}) {
  const [gstState, setGstState] = useState<GstState>({ status: "idle" });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [docPicks, setDocPicks] = useState<DocPicks>({ cc: null, ad: null, gst: null, udyam: null, labour: null });
  const gstInputRef = useRef<HTMLInputElement>(null);
  /** In-app CAPTCHA: same server session as gst.gov.in search (GET searchtp → captcha → POST). */
  const [captchaInline, setCaptchaInline] = useState<{
    sessionId: string;
    imageDataUrl: string;
    answer: string;
  } | null>(null);
  const [captchaLoadError, setCaptchaLoadError] = useState<string | null>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaVerifying, setCaptchaVerifying] = useState(false);
  const [ifscLookupHint, setIfscLookupHint] = useState<string | null>(null);
  const [ifscLookupBusy, setIfscLookupBusy] = useState(false);

  const gstInputVerifiedClass = gstState.status === "verified" ? "input--verified" : "";

  /* Auto-fill bank name + branch when IFSC is valid (Razorpay directory via our API). */
  useEffect(() => {
    const ifsc = draft.bankIfsc.trim().toUpperCase();
    if (!isValidSubcontractorIfsc(ifsc)) {
      setIfscLookupHint(null);
      setIfscLookupBusy(false);
      return;
    }
    let cancelled = false;
    const ac = new AbortController();
    setIfscLookupBusy(true);
    setIfscLookupHint(null);
    const debounceTimer = window.setTimeout(() => {
      void (async () => {
        try {
          const r = await lookupIfsc(ifsc, ac.signal);
          if (cancelled) return;
          setDraft((d) => {
            const cur = d.bankIfsc.trim().toUpperCase();
            if (cur !== ifsc) return d;
            return {
              ...d,
              bankName: r.bankName || d.bankName,
              bankBranchName: r.branchName || d.bankBranchName,
            };
          });
          if (!cancelled) {
            setIfscLookupHint(r.bankName || r.branchName ? IFSC_LOOKUP_OK_HINT : null);
          }
        } catch (e) {
          if (cancelled || (e instanceof Error && e.name === "AbortError")) return;
          setIfscLookupHint(e instanceof Error ? e.message : "IFSC lookup failed.");
        } finally {
          if (!cancelled) setIfscLookupBusy(false);
        }
      })();
    }, 450);
    return () => {
      cancelled = true;
      ac.abort();
      window.clearTimeout(debounceTimer);
      setIfscLookupBusy(false);
    };
  }, [draft.bankIfsc, setDraft]);

  /**
   * Manual refresh / retry — same as initial auto-load.
   * @param clearErrorFirst - set false when chaining after verify failure so the message stays until a new image loads.
   */
  async function fetchCaptchaImage(clearErrorFirst = true) {
    const gstin = draft.gstNumber.trim().toUpperCase();
    if (!isValidGstin(gstin)) {
      setCaptchaLoadError("Enter a valid 15-character GSTIN first.");
      return;
    }
    if (clearErrorFirst) setCaptchaLoadError(null);
    setCaptchaLoading(true);
    try {
      const { sessionId, imageDataUrl } = await fetchGstCaptchaSession();
      setCaptchaInline({ sessionId, imageDataUrl, answer: "" });
      setCaptchaLoadError(null);
    } catch (e) {
      setCaptchaLoadError(e instanceof Error ? e.message : "Could not load CAPTCHA.");
      setCaptchaInline(null);
    } finally {
      setCaptchaLoading(false);
    }
  }

  /* Auto-fetch CAPTCHA when GSTIN becomes valid (no “Load” click). */
  useEffect(() => {
    const gstin = draft.gstNumber.trim().toUpperCase();
    if (!isValidGstin(gstin)) return;
    let cancelled = false;
    (async () => {
      setCaptchaLoadError(null);
      setCaptchaLoading(true);
      try {
        const { sessionId, imageDataUrl } = await fetchGstCaptchaSession();
        if (!cancelled) setCaptchaInline({ sessionId, imageDataUrl, answer: "" });
      } catch (e) {
        if (!cancelled) {
          setCaptchaLoadError(e instanceof Error ? e.message : "Could not load CAPTCHA.");
          setCaptchaInline(null);
        }
      } finally {
        if (!cancelled) setCaptchaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setCaptchaLoading(false);
    };
  }, [draft.gstNumber]);

  async function handleVerifyWithCaptcha() {
    const gstin = draft.gstNumber.trim().toUpperCase();
    if (!captchaInline || !isValidGstin(gstin)) return;
    const answer = captchaInline.answer.trim();
    if (answer.length < 1) {
      setCaptchaLoadError("Type the characters from the image.");
      return;
    }
    setCaptchaLoadError(null);
    setCaptchaVerifying(true);
    try {
      const details = await verifyGstinWithCaptcha(captchaInline.sessionId, gstin, answer);
      setGstState({ status: "verified", details });
      setCaptchaInline(null);
      setDraft((d) => {
        const extra = (details.additionalAddresses ?? []).map((x) => x.trim()).filter(Boolean);
        const addrLines = [details.principalAddress?.trim(), ...extra].filter(Boolean);
        const registeredAddress = addrLines.length ? addrLines.join("\n\n") : d.registeredAddress;
        return {
          ...d,
          gstNumber: gstin,
          firmName: d.firmName.trim() || details.displayName || d.firmName,
          registeredAddress,
        };
      });
    } catch (e) {
      /* Session is consumed server-side; fetch a new CAPTCHA automatically. */
      setCaptchaInline(null);
      setCaptchaLoadError(e instanceof Error ? e.message : "CAPTCHA verification failed.");
      void fetchCaptchaImage(false);
    } finally {
      setCaptchaVerifying(false);
    }
  }

  async function handleSave() {
    setSaveError(null);
    if (!isFormComplete(draft, docPicks, mode)) return;
    setSaving(true);
    try {
      const base: Omit<SubcontractorMaster, "id" | "createdAt"> = {
        contactPersonName: draft.contactPersonName.trim(),
        firmName: draft.firmName.trim(),
        registeredAddress: draft.registeredAddress.trim(),
        firmRegistrationId: draft.firmRegistrationId.trim(),
        gstNumber: draft.gstNumber.trim().toUpperCase(),
        contactPhone: draft.contactPhone.trim(),
        contactEmail: draft.contactEmail.trim(),
        aadhaarNumber: normalizeSubcontractorAadhaarDigits(draft.aadhaarNumber),
        bankAccountNumber: draft.bankAccountNumber.trim(),
        bankIfsc: draft.bankIfsc.trim().toUpperCase(),
        bankBranchName: draft.bankBranchName.trim(),
        bankName: draft.bankName.trim(),
        bankAccountName: draft.bankAccountName.trim(),
        cancelChequeFileName: docPicks.cc?.name ?? draft.cancelChequeFileName ?? null,
        cancelChequeStored: draft.cancelChequeStored,
        aadharDocFileName: docPicks.ad?.name ?? draft.aadharDocFileName ?? null,
        aadharDocStored: draft.aadharDocStored,
        gstCertFileName: docPicks.gst?.name ?? draft.gstCertFileName ?? null,
        gstCertStored: draft.gstCertStored,
        udyamMsmeFileName: docPicks.udyam?.name ?? draft.udyamMsmeFileName ?? null,
        udyamMsmeStored: draft.udyamMsmeStored,
        labourLicenseFileName: docPicks.labour?.name ?? draft.labourLicenseFileName ?? null,
        labourLicenseStored: draft.labourLicenseStored,
      };
      if (docPicks.cc) {
        const { base64, mimeType } = await readFileAsRawBase64(docPicks.cc);
        base.cancelChequeBase64 = base64;
        base.cancelChequeMimeType = mimeType;
      }
      if (docPicks.ad) {
        const { base64, mimeType } = await readFileAsRawBase64(docPicks.ad);
        base.aadharDocBase64 = base64;
        base.aadharDocMimeType = mimeType;
      }
      if (docPicks.gst) {
        const { base64, mimeType } = await readFileAsRawBase64(docPicks.gst);
        base.gstCertBase64 = base64;
        base.gstCertMimeType = mimeType;
      }
      if (docPicks.udyam) {
        const { base64, mimeType } = await readFileAsRawBase64(docPicks.udyam);
        base.udyamMsmeBase64 = base64;
        base.udyamMsmeMimeType = mimeType;
      }
      if (docPicks.labour) {
        const { base64, mimeType } = await readFileAsRawBase64(docPicks.labour);
        base.labourLicenseBase64 = base64;
        base.labourLicenseMimeType = mimeType;
      }
      await onSave(base);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  const gstinUpper = draft.gstNumber.trim().toUpperCase();
  const gstinFormatOk = gstinUpper.length === 0 || isValidGstin(gstinUpper);
  const draftComplete = isFormComplete(draft, docPicks, mode);
  const apiBase = getApiBaseUrl();
  return (
    <div className="card card--gov card--elevated">
      <h2 className="card__title card__title--gov">
        {mode === "new" ? "Register new subcontractor" : "Edit subcontractor"}
      </h2>

      {/* ── Step 1: GSTIN (optional) ── */}
      <h3 className="form-section-title">
        Step 1 — GSTIN verification
        <span className="field-required-note"> (optional — use CAPTCHA when you have a GSTIN)</span>
      </h3>

      <div className="field field--full gst-verify-row">
        <label htmlFor="sc-gst">GSTIN (optional)</label>
        <input
          id="sc-gst"
          ref={gstInputRef}
          className={["mono", !gstinFormatOk ? "input--error" : "", gstInputVerifiedClass].filter(Boolean).join(" ")}
          value={draft.gstNumber}
            onChange={(e) => {
            setDraft((d) => ({ ...d, gstNumber: e.target.value.toUpperCase(), registeredAddress: "" }));
            setGstState({ status: "idle" });
            setCaptchaInline(null);
            setCaptchaLoadError(null);
          }}
          placeholder="e.g. 29ABCDE1234F1Z5"
          maxLength={15}
          autoComplete="off"
          spellCheck={false}
        />
        {!gstinFormatOk && gstinUpper.length > 0 && (
          <p className="field-hint field-hint--error">
            Invalid format. GSTIN must be 15 characters: 2 digits + 10-char PAN + 1 digit + Z + 1 char.
          </p>
        )}
      </div>

      {isValidGstin(gstinUpper) && (
        <div className="gst-captcha-inline" style={{ marginTop: "1rem" }}>
          {captchaLoading && !captchaInline ? (
            <span
              className="field-hint"
              style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
            >
              <span className="spinner spinner--sm" aria-hidden="true" />
              Loading CAPTCHA…
            </span>
          ) : null}
          {captchaInline ? (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem" }}>
                <img
                  src={captchaInline.imageDataUrl}
                  alt="CAPTCHA — type the characters shown"
                  style={{
                    display: "block",
                    maxHeight: "56px",
                    borderRadius: "4px",
                    border: "1px solid var(--gov-border, #e5e7eb)",
                  }}
                />
                <button
                  type="button"
                  className="btn btn--sm btn--link"
                  onClick={() => void fetchCaptchaImage()}
                  disabled={captchaLoading || captchaVerifying}
                >
                  Refresh
                </button>
              </div>
              <div className="toolbar-row" style={{ flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
                <input
                  id="gst-captcha-ans"
                  className="mono"
                  placeholder="Characters from image"
                  aria-label="CAPTCHA answer"
                  value={captchaInline.answer}
                  onChange={(e) =>
                    setCaptchaInline((c) => (c ? { ...c, answer: e.target.value } : null))
                  }
                  maxLength={16}
                  autoComplete="off"
                  spellCheck={false}
                  style={{ maxWidth: "14rem" }}
                />
                <button
                  type="button"
                  className="btn btn--sm btn--primary btn--tg"
                  onClick={() => void handleVerifyWithCaptcha()}
                  disabled={captchaVerifying || !captchaInline.answer.trim()}
                >
                  {captchaVerifying ? (
                    <>
                      <span className="spinner spinner--sm" aria-hidden="true" /> Verifying…
                    </>
                  ) : (
                    "Submit CAPTCHA & fetch details"
                  )}
                </button>
              </div>
            </>
          ) : null}
          {captchaLoadError ? (
            <p className="field-hint field-hint--error" style={{ margin: "0.25rem 0 0" }}>
              {captchaLoadError}
            </p>
          ) : null}
          {!captchaLoading && !captchaInline && captchaLoadError ? (
            <button
              type="button"
              className="btn btn--sm btn--tg-secondary"
              onClick={() => void fetchCaptchaImage()}
              disabled={captchaVerifying}
            >
              Try again
            </button>
          ) : null}
        </div>
      )}

      {gstState.status === "verified" && <GstVerifiedPanel details={gstState.details} />}

      {/* ── Step 2: Firm & contact ── */}
      <h3 className="form-section-title" style={{ marginTop: "1.5rem" }}>
        Step 2 — Firm, contact &amp; Aadhaar
        <span className="field-required-note"> (required fields marked)</span>
      </h3>

      <div className="field-grid">
        <div className="field">
          <label htmlFor="sc-firm-name">
            Firm / company name <span className="required-star" aria-hidden="true">*</span>
          </label>
          <input
            id="sc-firm-name"
            value={draft.firmName}
            onChange={(e) => setDraft((d) => ({ ...d, firmName: e.target.value }))}
            placeholder="Firm name (auto-filled from GST verification if used)"
          />
        </div>
        <div className="field field--full">
          <label htmlFor="sc-registered-address">Registered address (from GST)</label>
          <textarea
            id="sc-registered-address"
            value={draft.registeredAddress}
            onChange={(e) => setDraft((d) => ({ ...d, registeredAddress: e.target.value }))}
            placeholder="Filled after CAPTCHA verification, or type manually"
            rows={3}
            spellCheck={false}
          />
        </div>
        <div className="field">
          <label htmlFor="sc-contact-name">
            Authorised contact person <span className="required-star" aria-hidden="true">*</span>
          </label>
          <input
            id="sc-contact-name"
            value={draft.contactPersonName}
            onChange={(e) => setDraft((d) => ({ ...d, contactPersonName: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="sc-phone">
            Phone <span className="required-star" aria-hidden="true">*</span>
          </label>
          <input
            id="sc-phone"
            value={draft.contactPhone}
            onChange={(e) => setDraft((d) => ({ ...d, contactPhone: e.target.value }))}
            placeholder="10-digit mobile"
          />
        </div>
        <div className="field">
          <label htmlFor="sc-email">Email</label>
          <input
            id="sc-email"
            type="email"
            value={draft.contactEmail}
            onChange={(e) => setDraft((d) => ({ ...d, contactEmail: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="sc-aadhaar">
            Aadhaar number <span className="required-star" aria-hidden="true">*</span>
          </label>
          <input
            id="sc-aadhaar"
            className="mono"
            inputMode="numeric"
            autoComplete="off"
            value={draft.aadhaarNumber}
            onChange={(e) =>
              setDraft((d) => ({ ...d, aadhaarNumber: normalizeSubcontractorAadhaarDigits(e.target.value) }))
            }
            placeholder="12 digits"
            maxLength={14}
            spellCheck={false}
          />
          {draft.aadhaarNumber.length > 0 && draft.aadhaarNumber.length < 12 ? (
            <p className="field-hint field-hint--error">Enter all 12 digits.</p>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="sc-reg-id">Firm registration ID (CIN / LLP-IN)</label>
          <input
            id="sc-reg-id"
            className="mono"
            value={draft.firmRegistrationId}
            onChange={(e) => setDraft((d) => ({ ...d, firmRegistrationId: e.target.value }))}
            placeholder="Optional if GSTIN is provided"
          />
        </div>
      </div>

      {/* ── Step 3: Bank & mandatory documents ── */}
      <h3 className="form-section-title" style={{ marginTop: "1.5rem" }}>
        Step 3 — Bank account &amp; KYC documents
        <span className="field-required-note"> (required)</span>
      </h3>
      <p className="text-muted" style={{ fontSize: "0.85rem", marginBottom: "0.75rem" }}>
        Enter a valid IFSC to auto-fill bank and branch (public directory). Account holder name, account number, cancelled
        cheque, Aadhaar document, Udyam/MSME certificate, and labour licence are required. GST certificate is optional.
        Use PDF or clear photo scans (max size as allowed by your browser).
      </p>
      <div className="field-grid">
        <div className="field">
          <label htmlFor="sc-bank-acc">
            Account number <span className="required-star" aria-hidden="true">*</span>
          </label>
          <input
            id="sc-bank-acc"
            className="mono"
            value={draft.bankAccountNumber}
            onChange={(e) => setDraft((d) => ({ ...d, bankAccountNumber: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="sc-bank-ifsc">
            IFSC <span className="required-star" aria-hidden="true">*</span>
          </label>
          <input
            id="sc-bank-ifsc"
            className="mono"
            value={draft.bankIfsc}
            onChange={(e) => setDraft((d) => ({ ...d, bankIfsc: e.target.value.toUpperCase() }))}
            placeholder="e.g. SBIN0001234"
            maxLength={11}
            spellCheck={false}
          />
          {draft.bankIfsc.length > 0 && !isValidSubcontractorIfsc(draft.bankIfsc) ? (
            <p className="field-hint field-hint--error">IFSC must be 11 characters: 4 letters, digit 0, then 6 alphanumeric.</p>
          ) : null}
          {ifscLookupBusy && isValidSubcontractorIfsc(draft.bankIfsc.trim().toUpperCase()) ? (
            <p className="field-hint" style={{ marginTop: "0.25rem", display: "flex", alignItems: "center", gap: "0.35rem" }}>
              <span className="spinner spinner--sm" aria-hidden="true" />
              Looking up bank…
            </p>
          ) : null}
          {ifscLookupHint && !ifscLookupBusy ? (
            <p
              className={ifscLookupHint === IFSC_LOOKUP_OK_HINT ? "field-hint" : "field-hint field-hint--error"}
              style={{ marginTop: "0.25rem" }}
            >
              {ifscLookupHint}
            </p>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="sc-bank-branch">
            Branch name <span className="required-star" aria-hidden="true">*</span>
          </label>
          <input
            id="sc-bank-branch"
            value={draft.bankBranchName}
            onChange={(e) => setDraft((d) => ({ ...d, bankBranchName: e.target.value }))}
            placeholder="e.g. Habsiguda"
          />
        </div>
        <div className="field">
          <label htmlFor="sc-bank-inst">
            Bank name <span className="required-star" aria-hidden="true">*</span>
          </label>
          <input
            id="sc-bank-inst"
            value={draft.bankName}
            onChange={(e) => setDraft((d) => ({ ...d, bankName: e.target.value }))}
            placeholder="e.g. State Bank of India"
          />
        </div>
        <div className="field field--full">
          <label htmlFor="sc-bank-acc-name">
            Account holder name <span className="required-star" aria-hidden="true">*</span>
          </label>
          <input
            id="sc-bank-acc-name"
            value={draft.bankAccountName}
            onChange={(e) => setDraft((d) => ({ ...d, bankAccountName: e.target.value }))}
            placeholder="As on passbook or cheque"
          />
        </div>
        <div className="field field--full">
          <label htmlFor="sc-cancel-cheque">
            Cancelled cheque <span className="required-star" aria-hidden="true">*</span>
          </label>
          <input
            id="sc-cancel-cheque"
            type="file"
            accept=".pdf,image/*"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setDocPicks((p) => ({ ...p, cc: f }));
              if (f) setDraft((d) => ({ ...d, cancelChequeFileName: f.name }));
            }}
          />
          {docPicks.cc ? (
            <span className="text-muted file-hint">Selected: {docPicks.cc.name}</span>
          ) : draft.cancelChequeStored && draft.id ? (
            <div className="text-muted file-hint" style={{ marginTop: "0.35rem" }}>
              On file: {draft.cancelChequeFileName ?? "attachment"}{" "}
              <AuthenticatedFileLinks
                fileUrlBase={`${apiBase}/api/subcontractor-masters/${encodeURIComponent(draft.id)}/cancel-cheque-file`}
                fallbackFileName={draft.cancelChequeFileName ?? "cancel-cheque"}
              />
            </div>
          ) : null}
        </div>
        <div className="field field--full">
          <label htmlFor="sc-aadhar-doc">
            Aadhaar document (scan / PDF) <span className="required-star" aria-hidden="true">*</span>
          </label>
          <input
            id="sc-aadhar-doc"
            type="file"
            accept=".pdf,image/*"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setDocPicks((p) => ({ ...p, ad: f }));
              if (f) setDraft((d) => ({ ...d, aadharDocFileName: f.name }));
            }}
          />
          {docPicks.ad ? (
            <span className="text-muted file-hint">Selected: {docPicks.ad.name}</span>
          ) : draft.aadharDocStored && draft.id ? (
            <div className="text-muted file-hint" style={{ marginTop: "0.35rem" }}>
              On file: {draft.aadharDocFileName ?? "attachment"}{" "}
              <AuthenticatedFileLinks
                fileUrlBase={`${apiBase}/api/subcontractor-masters/${encodeURIComponent(draft.id)}/aadhar-doc-file`}
                fallbackFileName={draft.aadharDocFileName ?? "aadhar-doc"}
              />
            </div>
          ) : null}
        </div>
        <div className="field field--full">
          <label htmlFor="sc-gst-cert">GST registration certificate (optional)</label>
          <input
            id="sc-gst-cert"
            type="file"
            accept=".pdf,image/*"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setDocPicks((p) => ({ ...p, gst: f }));
              if (f) setDraft((d) => ({ ...d, gstCertFileName: f.name }));
            }}
          />
          {docPicks.gst ? (
            <span className="text-muted file-hint">Selected: {docPicks.gst.name}</span>
          ) : draft.gstCertStored && draft.id ? (
            <div className="text-muted file-hint" style={{ marginTop: "0.35rem" }}>
              On file: {draft.gstCertFileName ?? "attachment"}{" "}
              <AuthenticatedFileLinks
                fileUrlBase={`${apiBase}/api/subcontractor-masters/${encodeURIComponent(draft.id)}/gst-cert-file`}
                fallbackFileName={draft.gstCertFileName ?? "gst-certificate"}
              />
            </div>
          ) : null}
        </div>
        <div className="field field--full">
          <label htmlFor="sc-udyam-msme">
            Udyam / MSME <span className="required-star" aria-hidden="true">*</span>
          </label>
          <input
            id="sc-udyam-msme"
            type="file"
            accept=".pdf,image/*"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setDocPicks((p) => ({ ...p, udyam: f }));
              if (f) setDraft((d) => ({ ...d, udyamMsmeFileName: f.name }));
            }}
          />
          {docPicks.udyam ? (
            <span className="text-muted file-hint">Selected: {docPicks.udyam.name}</span>
          ) : draft.udyamMsmeStored && draft.id ? (
            <div className="text-muted file-hint" style={{ marginTop: "0.35rem" }}>
              On file: {draft.udyamMsmeFileName ?? "attachment"}{" "}
              <AuthenticatedFileLinks
                fileUrlBase={`${apiBase}/api/subcontractor-masters/${encodeURIComponent(draft.id)}/udyam-msme-file`}
                fallbackFileName={draft.udyamMsmeFileName ?? "udyam-msme"}
              />
            </div>
          ) : null}
        </div>
        <div className="field field--full">
          <label htmlFor="sc-labour-license">
            Labour licence <span className="required-star" aria-hidden="true">*</span>
          </label>
          <input
            id="sc-labour-license"
            type="file"
            accept=".pdf,image/*"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setDocPicks((p) => ({ ...p, labour: f }));
              if (f) setDraft((d) => ({ ...d, labourLicenseFileName: f.name }));
            }}
          />
          {docPicks.labour ? (
            <span className="text-muted file-hint">Selected: {docPicks.labour.name}</span>
          ) : draft.labourLicenseStored && draft.id ? (
            <div className="text-muted file-hint" style={{ marginTop: "0.35rem" }}>
              On file: {draft.labourLicenseFileName ?? "attachment"}{" "}
              <AuthenticatedFileLinks
                fileUrlBase={`${apiBase}/api/subcontractor-masters/${encodeURIComponent(draft.id)}/labour-license-file`}
                fallbackFileName={draft.labourLicenseFileName ?? "labour-license"}
              />
            </div>
          ) : null}
        </div>
      </div>

      {saveError && (
        <p className="alert alert--danger" role="alert" style={{ marginTop: "0.75rem" }}>
          {saveError}
        </p>
      )}

      {!draftComplete && (
        <p className="text-muted" style={{ fontSize: "0.8rem", marginTop: "0.75rem" }}>
          Required: firm and contact details, 12-digit Aadhaar, account number, IFSC, branch name, bank name, account
          holder name, cancelled cheque, Aadhaar document, Udyam/MSME, and labour licence. If you enter a GSTIN it must
          be valid. GST certificate is optional.
        </p>
      )}

      <div className="toolbar-row" style={{ marginTop: "1.25rem" }}>
        <button type="button" className="btn btn--tg-secondary btn--sm" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn--primary btn--tg"
          onClick={() => void handleSave()}
          disabled={!draftComplete || saving}
        >
          {saving ? "Saving…" : "Save firm"}
        </button>
      </div>
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────── */

export function SubcontractorOnboarding() {
  const { subcontractorMasters, addSubcontractorMaster, updateSubcontractorMaster, deleteSubcontractorMaster } =
    useAppState();
  const [mode, setMode] = useState<"list" | "new" | "edit">("list");
  const [draft, setDraft] = useState<Draft>(emptyDraft());

  const sortedMasters = useMemo(
    () => [...subcontractorMasters].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [subcontractorMasters],
  );

  useEffect(() => {
    if (mode === "list") setDraft(emptyDraft());
  }, [mode]);

  function startEdit(m: SubcontractorMaster) {
    setDraft({
      id: m.id,
      contactPersonName: m.contactPersonName,
      firmName: m.firmName,
      registeredAddress: m.registeredAddress ?? "",
      firmRegistrationId: m.firmRegistrationId,
      gstNumber: m.gstNumber,
      contactPhone: m.contactPhone,
      contactEmail: m.contactEmail,
      aadhaarNumber: m.aadhaarNumber ?? "",
      bankAccountNumber: m.bankAccountNumber,
      bankIfsc: m.bankIfsc,
      bankBranchName: m.bankBranchName ?? "",
      bankName: m.bankName ?? "",
      bankAccountName: m.bankAccountName ?? "",
      cancelChequeFileName: m.cancelChequeFileName ?? null,
      cancelChequeStored: Boolean(m.cancelChequeStored),
      aadharDocFileName: m.aadharDocFileName ?? null,
      aadharDocStored: Boolean(m.aadharDocStored),
      gstCertFileName: m.gstCertFileName ?? null,
      gstCertStored: Boolean(m.gstCertStored),
      udyamMsmeFileName: m.udyamMsmeFileName ?? null,
      udyamMsmeStored: Boolean(m.udyamMsmeStored),
      labourLicenseFileName: m.labourLicenseFileName ?? null,
      labourLicenseStored: Boolean(m.labourLicenseStored),
    });
    setMode("edit");
  }

  async function saveMaster(row: Omit<SubcontractorMaster, "id" | "createdAt">) {
    if (mode === "new") {
      const id = await addSubcontractorMaster(row);
      if (!id) return;
    } else if (mode === "edit" && draft.id) {
      const ok = await updateSubcontractorMaster(draft.id, row);
      if (!ok) return;
    }
    setMode("list");
  }

  async function handleDeleteMaster(m: SubcontractorMaster) {
    const ok = window.confirm(
      `Delete “${m.firmName}” from the subcontractor master list?\n\nAny applicant dossiers linked to this firm will be unlinked (you can pick another firm later). This cannot be undone.`,
    );
    if (!ok) return;
    await deleteSubcontractorMaster(m.id);
  }

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Subcontractor Onboarding" }]}
        title="Subcontractor Onboarding"
        subtitle="Register and manage firms engaged as subcontractors for Indiramma Indlu beneficiaries."
      />

      {mode === "list" ? (
        <>
          <div className="toolbar-row" style={{ marginTop: 0, marginBottom: "1rem" }}>
            <button
              type="button"
              className="btn btn--primary btn--tg"
              onClick={() => { setDraft(emptyDraft()); setMode("new"); }}
            >
              + Register new subcontractor
            </button>
          </div>

          <div className="card card--flush card--elevated">
            <div className="table-toolbar table-toolbar--stack">
              <div className="table-toolbar__top">
                <span className="table-toolbar__count">{subcontractorMasters.length} firms onboarded</span>
              </div>
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Firm</th>
                    <th>Authorised contact</th>
                    <th>Phone</th>
                    <th>GSTIN</th>
                    <th>Reg. ID</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sortedMasters.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="data-table__empty">
                        No subcontractors registered yet. Click "Register new subcontractor" to add the first firm.
                      </td>
                    </tr>
                  ) : (
                    sortedMasters.map((m) => (
                      <tr key={m.id}>
                        <td><span className="table-name">{m.firmName}</span></td>
                        <td>{m.contactPersonName}</td>
                        <td className="mono">{m.contactPhone}</td>
                        <td className="mono text-muted">{m.gstNumber || "—"}</td>
                        <td className="mono text-muted">{m.firmRegistrationId || "—"}</td>
                        <td>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", justifyContent: "flex-end" }}>
                            {m.portalToken && (
                              <button
                                type="button"
                                className="btn btn--tg-secondary btn--sm"
                                onClick={() => {
                                  const url = `${window.location.origin}/subcontractor-portal/${m.portalToken}`;
                                  navigator.clipboard.writeText(url);
                                  alert("Portal link copied to clipboard!\n" + url);
                                }}
                              >
                                Copy Portal Link
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn--tg-secondary btn--sm"
                              onClick={() => startEdit(m)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn--danger btn--sm"
                              onClick={() => void handleDeleteMaster(m)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="section-lead" style={{ marginTop: "1.25rem" }}>
            <Link to="/applications">Applicants</Link>
            {" — "}
            open a dossier, then <strong>Subcontractor</strong> to link an onboarded firm.
          </p>
        </>
      ) : (
        <SubcontractorForm
          key={`${mode}-${draft.id || "new"}`}
          mode={mode}
          draft={draft}
          setDraft={setDraft}
          onSave={saveMaster}
          onCancel={() => setMode("list")}
        />
      )}
    </>
  );
}
